import { Button, GridColumn, GridRow, Icon, ToastLevel, useIntl, useToast, WizardProcessIndicator } from "@metsooutotec/modes-react-components"
import { useEffect, useState } from "react"
import Draggable from "react-draggable"
import { ReactSVG } from "react-svg"
import linersLocationsStores from "features/organizations/asset-sections/store/liner-locations-store"
import { AssetSectionSettings } from "../asset-section-editor-settings"
import { CssSVGClasses, EditorSteps, OnSvgClickedFunctions, Position, StageButtonsFunctions } from "../editor-utils/editor-types"
import { StripLastSegment } from "../editor-utils/id-string-utils"
import { AssignIncrementalIdsToHolesInLiners, GetLinerRegionReferenceFromHoleId } from "../helpers/holes-helper"
import { GetSurroundingLinerID, MapLiners } from "../helpers/liners-helper"
import { AssignIncrementalIdsToDrawingReferencesToLiners } from "../helpers/references-helper"
import { ResetSelectedPath, fetchSvgImageFromUrl, updateAssetSectionImage } from "../helpers/svg-helpers"
import { GetLinerLocationInfo } from "../helpers/wear-reference-plane-helper"
import { generateSteps } from "./steps-generator"
import { EditSvgBoxComponent } from "../edit-box/svg-edit-box"
import asEditorStore from "./../local-store/asset-section-editor-store"
import { observer } from "mobx-react-lite"
import { EditingAssetSectionWindow, LinerLocation } from "features/organizations/asset-sections/models/asset-section-models"
import LowSpinner from "components/common/low-spinner"

const svgSteps = [
    { step: EditorSteps.LinersLocation, message: "assetSectionEditor.stepLiners", buttonLabel: "Map Liners", widthSize: "25%" },
    { step: EditorSteps.WearReferencePlane, message: "assetSectionEditor.stepLinerThickness", buttonLabel: "Map Thicknesses", widthSize: "25%" },
    { step: EditorSteps.HolesLocation, message: "assetSectionEditor.stepHoles", buttonLabel: "Map Holes", widthSize: "25%" },
    {
        step: EditorSteps.LinersDrawingReferences,
        message: "assetSectionEditor.stepLinerReferences",
        buttonLabel: "Auto Drawing References",
        widthSize: "35%",
    },
]

interface SecondColumnParams {
    loadingAssetImage: boolean
    assetSectionId: string | undefined
    assetSectionSettings: AssetSectionSettings
    newEditAssetSection: EditingAssetSectionWindow
    setIsModalGlobalWearReferencePlaneOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const SecondColumnEditor: React.FC<SecondColumnParams> = observer(params => {
    const { loadingAssetImage, assetSectionId, assetSectionSettings, newEditAssetSection, setIsModalGlobalWearReferencePlaneOpen } = params

    const linerLocations: LinerLocation[] = linersLocationsStores.GetLinerLocationsByAssetSectionId(assetSectionId || "")
    const { setMessage } = useToast()
    const { formatMessage } = useIntl()
    const { selectedLiners, setSelectedLiners } = asEditorStore
    const { selectedWearReferencePlane } = asEditorStore
    const { selectedholes, setSelectedHoles } = asEditorStore
    const { selectedLinersDrawingRef, setSelectedLinersDrawingRef } = asEditorStore
    const { assetSectionImage, setAssetSectionImage } = asEditorStore
    const { setSelectedPathId } = asEditorStore
    const { firstLinerName, firstDrawingReferenceName, setEditBoxInputValue } = asEditorStore

    const [zoomLevel, setZoomLevel] = useState(1)
    const [svgCurrentPosition, setSvgCurrentPosition] = useState<Position>({ x: 0, y: 0 })
    // Used to render the string inside the tooltip
    const [tooltipText, setTooltipText] = useState<string>("")
    const [boxPosition, setBoxPosition] = useState<Position>({ x: 0, y: 0 })

    const steps = generateSteps(selectedLiners, selectedWearReferencePlane, selectedholes, selectedLinersDrawingRef)

    useEffect(() => {
        asEditorStore.resetUIStates()
    }, [asEditorStore.currentStep])

    const setBoxPositionBasedOnPath = (path: SVGPathElement) => {
        const pathRect = path.getBoundingClientRect()
        const containerRect = (document.querySelector(".zoomable-svg-container") as HTMLElement).getBoundingClientRect()
        const adjustedX = pathRect.left - containerRect.left
        const adjustedY = pathRect.top - containerRect.top
        setBoxPosition({ x: adjustedX, y: adjustedY })
    }

    const onShowEditBoxSetter = (path: SVGPathElement, editBixValue: string) => {
        path.classList.add(CssSVGClasses.SelectedPath)
        setEditBoxInputValue(editBixValue)
    }

    const onShowEditBox: OnSvgClickedFunctions = {
        // Working with liners.
        [EditorSteps.LinersLocation]: (path: SVGPathElement) => {
            onShowEditBoxSetter(path, firstLinerName)
        },
        [EditorSteps.WearReferencePlane]: (path: SVGPathElement) => {
            onShowEditBoxSetter(path, "")
        },
        [EditorSteps.HolesLocation]: (path: SVGPathElement) => {
            onShowEditBoxSetter(path, "")
        },
        [EditorSteps.LinersDrawingReferences]: (path: SVGPathElement) => {
            if (path.classList.contains(CssSVGClasses.WsLiner)) {
                onShowEditBoxSetter(path, firstDrawingReferenceName)
            }
        },
    }

    // #endregion

    // #region map liners, map holes, and auto references buttons

    const stageButtonOnClick: StageButtonsFunctions = {
        [EditorSteps.LinersLocation]: async () => {
            //Map Liners
            const { svgDocument, renderedSvg } = await fetchSvgImageFromUrl(assetSectionImage as string)
            MapLiners(linerLocations, svgDocument, renderedSvg as SVGSVGElement, selectedLiners, setSelectedLiners, assetSectionSettings.lineWeight)
            const blobUrl = await updateAssetSectionImage(svgDocument)
            await setAssetSectionImage(blobUrl)
        },
        [EditorSteps.WearReferencePlane]: async () => {
            setIsModalGlobalWearReferencePlaneOpen(true)
        },
        [EditorSteps.HolesLocation]: async () => {
            //Map Holes
            const { svgDocument, renderedSvg } = await fetchSvgImageFromUrl(assetSectionImage as string)
            AssignIncrementalIdsToHolesInLiners(
                assetSectionId as string,
                svgDocument,
                renderedSvg as SVGSVGElement,
                selectedholes,
                setSelectedHoles,
                newEditAssetSection.sensorNotificationArea,
            )
            const blobUrl = await updateAssetSectionImage(svgDocument)
            await setAssetSectionImage(blobUrl)
        },
        [EditorSteps.LinersDrawingReferences]: async () => {
            const { svgDocument, renderedSvg } = await fetchSvgImageFromUrl(assetSectionImage as string)
            AssignIncrementalIdsToDrawingReferencesToLiners(
                linerLocations,
                svgDocument,
                renderedSvg as SVGSVGElement,
                selectedLinersDrawingRef,
                setSelectedLinersDrawingRef,
            )
            const blobUrl = await updateAssetSectionImage(svgDocument)
            await setAssetSectionImage(blobUrl)
        },
    }
    // #endregion
    // #region Zoom Buttons
    const zoomButtonsHandlers = {
        zoomIn: () => {
            setZoomLevel(zoomLevel + 0.1)
        },
        zoomOut: () => {
            if (zoomLevel > 0.2) {
                setZoomLevel(zoomLevel - 0.1)
            }
        },
        reset: () => {
            setZoomLevel(1)
            setSvgCurrentPosition({ x: 0, y: 0 })
        },
    }
    // #endregion

    const mouseReactions = {
        onMouseOver: (e: React.MouseEvent<SVGElement, MouseEvent>) => {
            const path = e.target as SVGElement
            const pathId = path.id || "" // Provide a fallback value

            setTooltipText("")
            if (path instanceof SVGPathElement) {
                if (path.classList.contains(CssSVGClasses.WsLiner)) {
                    const linerLocationInfo = GetLinerLocationInfo(linerLocations, pathId)
                    if (!linerLocationInfo) return
                    setTooltipText(
                        `Liner Ref: ${linerLocationInfo.reference as string}, WearRefPlane: ${linerLocationInfo.wallThickness},  DrawingRef: ${
                            linerLocationInfo.drawingReference
                        }`,
                    )
                } else if (path.classList.contains(CssSVGClasses.WsHole)) {
                    const svg = document.querySelector("svg") as SVGSVGElement
                    const surroundingLinerId = GetSurroundingLinerID(path, svg) ?? ""
                    const linerLocation = linerLocations.find(l => l.reference.toLowerCase() === surroundingLinerId.trim().toLowerCase())
                    const linerRegionReference = GetLinerRegionReferenceFromHoleId(pathId)
                    const linerRegion = linersLocationsStores.GetLinerRegionByLinerRegionRef(linerLocation?.id ?? "", linerRegionReference ?? "")
                    const holeName = StripLastSegment(pathId)
                    setTooltipText(`HoleId: ${holeName as string}, LinerRegion Name: ${linerRegion?.name ?? ""}`)
                }
            }
        },
        onMouseLeave: () => {
            setTooltipText("")
        },
        onMouseDown: (e: React.MouseEvent<SVGElement, MouseEvent>, div: SVGSVGElement) => {
            if (asEditorStore.selectedPathId) ResetSelectedPath(asEditorStore.selectedPathId)
            asEditorStore.restEditBox()

            if (asEditorStore.currentStep === EditorSteps.SVGSettings) return

            const svg = div.querySelector("svg") as SVGSVGElement
            const path = e.target as SVGElement

            if (!(path instanceof SVGPathElement)) return

            if (!path.id) {
                setMessage("This path does not have an Id to be selected", ToastLevel.WARNING, 1000)
                return
            }

            const processPath = (path: SVGPathElement) => {
                setSelectedPathId(path.id)
                // Do the proccess
                onShowEditBox[asEditorStore.currentStep](path)
                // set Box position
                setBoxPositionBasedOnPath(path)
                asEditorStore.setShowEditBox(true)
            }

            switch (asEditorStore.currentStep) {
                case EditorSteps.LinersLocation:
                    if (path.id.toLowerCase().startsWith("hole")) {
                        setMessage(`${path.id} is a Hole not a Liner!`, ToastLevel.INFO, 3000)
                        return
                    }
                    processPath(path)
                    break

                case EditorSteps.WearReferencePlane:
                    if (path.id.toLowerCase().startsWith("hole")) {
                        setMessage(`${path.id} is a Hole not a Liner!`, ToastLevel.INFO, 3000)
                        return
                    }
                    processPath(path)
                    break

                case EditorSteps.HolesLocation:
                    const surroundingLinerId = GetSurroundingLinerID(path, svg)
                    asEditorStore.setSurroundingHoleLiner(surroundingLinerId as string)
                    processPath(path)
                    break

                case EditorSteps.LinersDrawingReferences:
                    if (path.classList.contains(CssSVGClasses.WsLiner)) {
                        processPath(path)
                    } else {
                        setMessage(`${path.id} does not contain ${CssSVGClasses.WsLiner} class to be clicked!`, ToastLevel.INFO, 3000)
                        return
                    }
                    break
            }
        },
    }

    return (
        <GridColumn className='second-column' stretched>
            <GridRow className='first-row' style={{ display: asEditorStore.currentStep === EditorSteps.SVGSettings ? "contents" : "flex" }}>
                <WizardProcessIndicator
                    className='wizard-component'
                    currentStep={asEditorStore.currentStep}
                    steps={steps.map(step => ({
                        key: step.key.toString(),
                        text: step.text,
                    }))}
                />
                {svgSteps.map(({ step, buttonLabel, widthSize }) =>
                    asEditorStore.currentStep === step ? (
                        <Button
                            style={{ width: widthSize, justifyContent: "center" }}
                            onClick={stageButtonOnClick[asEditorStore.currentStep]}
                            primary
                        >
                            {buttonLabel}
                        </Button>
                    ) : null,
                )}
            </GridRow>

            <GridRow stretched className='second-row'>
                {loadingAssetImage ? (
                    <LowSpinner id='assetSection.imageLoading' />
                ) : (
                    <>
                        <Draggable
                            position={svgCurrentPosition}
                            onDrag={(e, data) => {
                                setSvgCurrentPosition({ x: data.x, y: data.y })
                            }}
                        >
                            <div className='zoomable-svg-container'>
                                <div
                                    onMouseDown={e =>
                                        mouseReactions.onMouseDown(
                                            e as unknown as React.MouseEvent<SVGElement, MouseEvent>,
                                            e.currentTarget as unknown as SVGSVGElement,
                                        )
                                    }
                                    onMouseOver={e => mouseReactions.onMouseOver(e as unknown as React.MouseEvent<SVGElement, MouseEvent>)}
                                    onMouseLeave={mouseReactions.onMouseLeave}
                                    style={{
                                        minHeight: "30em",
                                        width: "100%",
                                        height: "100%",
                                        transform: `scale(${zoomLevel})`,
                                        transformOrigin: "top left",
                                    }}
                                >
                                    <ReactSVG src={assetSectionImage as string} useRequestCache={false} className='wrapperEditor' />
                                </div>
                            </div>
                        </Draggable>
                        {asEditorStore.showEditBox && boxPosition && (
                            <EditSvgBoxComponent
                                assetSectionId={assetSectionId as string}
                                boxPosition={boxPosition}
                                linerLocations={linerLocations}
                                svgCurrentPosition={svgCurrentPosition}
                            />
                        )}
                        {tooltipText && <div className='tooltip'>{tooltipText}</div>}
                        <div className='zoom-buttons-container'>
                            <Icon className='icon-zoom-out-24 zoom-button' onClick={zoomButtonsHandlers.zoomOut} />
                            <Icon className='icon-refresh-24 zoom-button' onClick={zoomButtonsHandlers.reset} />
                            <Icon className='icon-zoom-in-24 zoom-button' onClick={zoomButtonsHandlers.zoomIn} />
                        </div>
                    </>
                )}
            </GridRow>

            {svgSteps.map(({ step, message }) =>
                asEditorStore.currentStep === step ? (
                    <GridRow key={step} className='third-row'>
                        <Icon className='icon-info-o-24' />
                        <p className='instructionNotes'>{formatMessage(message)}</p>
                    </GridRow>
                ) : null,
            )}
        </GridColumn>
    )
})
